import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { usePromoCustomerGroups, usePromoSalesChannels, usePromotion, useSimulatePromoCart } from '../../hooks';
import { formatPrice } from '../../lib/format';
import { ProductAutocomplete } from './ProductAutocomplete';
import { ChipListInput } from './ChipListInput';
import type { ProductRef, SimulateResponse } from '../../model/types';

interface BasketRow {
  product: ProductRef;
  qty: number;
}

/** "Simulator" — test-basket evaluation (plan-011 §8.8). Side-effect-free, `view` is enough. */
export function SimulatorPanel() {
  const { t } = useTranslation('promotions');
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkPromotionId = searchParams.get('promotionId') ?? undefined;
  const deepLinkPromotion = usePromotion(deepLinkPromotionId, { enabled: Boolean(deepLinkPromotionId) });
  const [dismissedDeepLink, setDismissedDeepLink] = useState(false);

  const [basket, setBasket] = useState<BasketRow[]>([]);
  const [channelCode, setChannelCode] = useState('');
  const [customerGroupCode, setCustomerGroupCode] = useState('');
  const [customerKind, setCustomerKind] = useState<'ANONYMOUS' | 'REGISTERED'>('REGISTERED');
  const [at, setAt] = useState('');
  const [couponCodes, setCouponCodes] = useState<string[]>([]);
  const [linesExpanded, setLinesExpanded] = useState(false);

  const channels = usePromoSalesChannels();
  const groups = usePromoCustomerGroups();
  const simulate = useSimulatePromoCart();

  const canRun = basket.length > 0 && channelCode.length > 0 && !simulate.isPending;

  const addProducts = (products: ProductRef[]) => {
    setBasket((prev) => {
      const next = [...prev];
      for (const product of products) {
        if (!next.some((row) => row.product.id === product.id)) {
          next.push({ product, qty: 1 });
        }
      }
      return next;
    });
  };

  const setQty = (productId: string, qty: number) => {
    setBasket((prev) => prev.map((row) => (row.product.id === productId ? { ...row, qty: Math.max(1, qty) } : row)));
  };

  const removeRow = (productId: string) => setBasket((prev) => prev.filter((row) => row.product.id !== productId));

  const handleRun = () => {
    simulate.mutate({
      channelCode,
      customer: {
        kind: customerKind,
        groupCodes: customerGroupCode ? [customerGroupCode] : [],
      },
      couponCodes: couponCodes.length ? couponCodes.map((c) => c.toUpperCase()) : undefined,
      lines: basket.map((row) => ({ productId: row.product.id, qty: row.qty, selected: true })),
      at: at ? new Date(at).toISOString() : undefined,
    });
  };

  const result: SimulateResponse | undefined = simulate.data;

  return (
    <Stack spacing={2.5}>
      {deepLinkPromotionId && !dismissedDeepLink ? (
        <Alert
          severity="info"
          onClose={() => {
            setDismissedDeepLink(true);
            searchParams.delete('promotionId');
            setSearchParams(searchParams, { replace: true });
          }}
        >
          {t('simulator.checkingPromotion', { name: deepLinkPromotion.data?.name ?? deepLinkPromotionId })}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
        <Card variant="outlined" sx={{ flex: 1, width: '100%' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {t('simulator.basketCardTitle')}
            </Typography>
            <ProductAutocomplete label={t('simulator.addProductLabel')} value={[]} onChange={addProducts} />
            {basket.length > 0 ? (
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('simulator.table.product')}</TableCell>
                      <TableCell align="right">{t('simulator.table.price')}</TableCell>
                      <TableCell align="center">{t('simulator.table.qty')}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {basket.map((row) => (
                      <TableRow key={row.product.id}>
                        <TableCell>{row.product.name}</TableCell>
                        <TableCell align="right">{formatPrice(row.product.price)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            <IconButton size="small" onClick={() => setQty(row.product.id, row.qty - 1)} aria-label={t('simulator.decreaseQty')}>
                              <RemoveRoundedIcon fontSize="inherit" />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
                              {row.qty}
                            </Typography>
                            <IconButton size="small" onClick={() => setQty(row.product.id, row.qty + 1)} aria-label={t('simulator.increaseQty')}>
                              <AddRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" aria-label={t('simulator.removeRow')} onClick={() => removeRow(row.product.id)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('simulator.emptyBasketHint')}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, width: '100%' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {t('simulator.contextCardTitle')}
            </Typography>
            <Stack spacing={2}>
              <TextField
                select
                label={t('simulator.channelLabel')}
                required
                value={channelCode}
                onChange={(e) => setChannelCode(e.target.value)}
                fullWidth
              >
                {(channels.data ?? []).map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('simulator.customerGroupLabel')}
                value={customerGroupCode}
                onChange={(e) => setCustomerGroupCode(e.target.value)}
                fullWidth
              >
                <MenuItem value="">{t('promotionsList.noCampaign')}</MenuItem>
                {(groups.data ?? []).map((g) => (
                  <MenuItem key={g.code} value={g.code}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('simulator.customerKindLabel')}
                value={customerKind}
                onChange={(e) => setCustomerKind(e.target.value as 'ANONYMOUS' | 'REGISTERED')}
                fullWidth
              >
                <MenuItem value="ANONYMOUS">{t('conditionEditor.customerKind.ANONYMOUS')}</MenuItem>
                <MenuItem value="REGISTERED">{t('conditionEditor.customerKind.REGISTERED')}</MenuItem>
              </TextField>
              <TextField
                label={t('simulator.atLabel')}
                type="datetime-local"
                value={at}
                onChange={(e) => setAt(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <ChipListInput
                label={t('simulator.couponsLabel')}
                value={couponCodes}
                onChange={setCouponCodes}
                placeholder={t('simulator.couponsPlaceholder')}
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Box sx={{ textAlign: 'center' }}>
        <Button variant="contained" size="large" startIcon={<PlayArrowRoundedIcon />} disabled={!canRun} onClick={handleRun}>
          {t('simulator.run')}
        </Button>
      </Box>

      {simulate.isPending ? (
        <LoadingState label={t('simulator.running')} />
      ) : simulate.isError ? (
        <ErrorState
          title={t('simulator.runError.title')}
          message={simulate.error?.message ?? t('common:error.unknown')}
          onRetry={handleRun}
        />
      ) : result ? (
        <Stack spacing={2.5}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap justifyContent="space-around">
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('simulator.totals.subtotal')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatPrice(result.totals.subtotal)}
                  </Typography>
                </Stack>
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('simulator.totals.discount')}
                  </Typography>
                  <Typography variant="h6" color="error.main" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    −{formatPrice(result.totals.discountTotal)}
                  </Typography>
                </Stack>
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('simulator.totals.delivery')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatPrice(result.totals.deliveryTotal)}
                  </Typography>
                </Stack>
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('simulator.totals.grandTotal')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatPrice(result.totals.grandTotal)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('simulator.firedTitle')}
              </Typography>
              {result.firedPromotions.length === 0 ? (
                <Typography variant="body2" color="success.main">
                  {t('simulator.noneFired')}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {result.firedPromotions.map((fired) => (
                    <Stack key={fired.promotionId} direction="row" spacing={1} alignItems="center">
                      <CheckCircleRoundedIcon fontSize="small" color="success" />
                      <Typography variant="body2">
                        {fired.name} · {fired.level} · −{formatPrice(fired.amount)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {result.skippedPromotions.length > 0 ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('simulator.skippedTitle')}
                </Typography>
                <Stack spacing={1}>
                  {result.skippedPromotions.map((skipped) => (
                    <Stack key={skipped.promotionId} direction="row" spacing={1} alignItems="center">
                      <CancelRoundedIcon fontSize="small" color="disabled" />
                      <Typography variant="body2" color="text.secondary">
                        {skipped.reason}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : null}

          {result.rejectedCoupons.length > 0 ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('simulator.rejectedCouponsTitle')}
                </Typography>
                <Stack spacing={1}>
                  {result.rejectedCoupons.map((rejected) => (
                    <Chip
                      key={rejected.code}
                      size="small"
                      color="error"
                      variant="outlined"
                      label={`${rejected.code} — ${rejected.reason}`}
                      sx={{ alignSelf: 'flex-start', fontFamily: 'JetBrains Mono, monospace' }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : null}

          <Card variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => setLinesExpanded((v) => !v)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('simulator.linesTitle')}
                </Typography>
                <IconButton
                  size="small"
                  aria-expanded={linesExpanded}
                  aria-label={t('simulator.toggleLines')}
                  sx={{ transform: linesExpanded ? 'rotate(180deg)' : undefined }}
                >
                  <ExpandMoreRoundedIcon />
                </IconButton>
              </Stack>
              <Collapse in={linesExpanded}>
                <TableContainer sx={{ mt: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('simulator.table.product')}</TableCell>
                        <TableCell align="right">{t('simulator.table.qty')}</TableCell>
                        <TableCell align="right">{t('simulator.lines.unit')}</TableCell>
                        <TableCell align="right">{t('simulator.lines.final')}</TableCell>
                        <TableCell align="right">{t('simulator.lines.total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.lines.map((line) => (
                        <TableRow key={line.productId}>
                          <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{line.productId}</TableCell>
                          <TableCell align="right">{line.qty}</TableCell>
                          <TableCell align="right">{formatPrice(line.unitAmount)}</TableCell>
                          <TableCell align="right">{formatPrice(line.finalUnitAmount)}</TableCell>
                          <TableCell align="right">{formatPrice(line.lineTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </CardContent>
          </Card>
        </Stack>
      ) : (
        <EmptyState title={t('simulator.preRunEmpty.title')} description={t('simulator.preRunEmpty.description')} />
      )}

      <FormControlLabel
        // Kept out of the request body deliberately — `SimulateRequest` has no "include drafts"
        // flag in the shipped contract, so this checkbox can't actually change server behaviour.
        // Rendered disabled with an explanatory hint rather than silently doing nothing.
        control={<Checkbox checked={false} disabled />}
        label={
          <Typography variant="caption" color="text.secondary">
            {t('simulator.includeDraftsUnavailable')}
          </Typography>
        }
        sx={{ mt: -1 }}
      />
    </Stack>
  );
}
